import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridRowParams,
  GridSortDirection,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { t } from 'i18next';
import { toNumber } from 'lodash';

import { ColumnDensity, SORT_ORDER } from '@common/types';
import { MAX_PINNED_COLUMNS } from '@common/types/constants';
import { theme } from '@store';
import {
  IActivityLogsTableStore,
  PersistedActivityLogsColumn,
  useActivityLogsTableStore,
} from 'store/activityLogsTable.store';
import { TruncatedTextWithConditionalTooltip } from '@components';
import { formatDateShort } from '@util/date.ts';
import { buildGridColumns, getReorderedColumns } from '@util/dataGrid';

import { ActivityLogsRowActions } from '../components/ActivityLogsRowActions';

export interface IActivityLogsTableFilters {
  sort_by: ActivityLogsColumnNames;
  sort_order: SORT_ORDER;
  page: number;
  limit: number;
  search?: string;
}

export type ActivityLogsColumnNames =
  | 'title'
  | 'timestamp'
  | 'recorded_minutes'
  | 'provider'
  | 'created_at'
  | 'related_entity';

export interface IActivityLogsTableEntry {
  id: string;
  timestamp: string;
  title: string;
  created_at: string;
  recorded_minutes: number | null;
  provider_name: string;
  provider_practice_name: string;
  entity_names: string;
  entity_types: string;
  description: string;
  editable: boolean;
  patient_id: string;
}

interface IUseActivityLogsTableProps {
  filters: IActivityLogsTableFilters;
  setFilters: (filters: IActivityLogsTableFilters) => void;
  disabled: boolean;
}

export const useActivityLogsTable = ({
  filters,
  setFilters,
  disabled,
}: IUseActivityLogsTableProps) => {
  const apiRef = useGridApiRef();
  const density: ColumnDensity = useActivityLogsTableStore(
    (state: IActivityLogsTableStore) => state.density,
  );
  const setDensity = useActivityLogsTableStore(
    (state: IActivityLogsTableStore) => state.setDensity,
  );
  const resetView = useActivityLogsTableStore(
    (state: IActivityLogsTableStore) => state.resetView,
  );
  const persistedColumns = useActivityLogsTableStore(
    (state: IActivityLogsTableStore) => state.columns,
  );
  const setPersistedColumns = useActivityLogsTableStore(
    (state: IActivityLogsTableStore) => state.setColumns,
  );
  const pinnedColumns = useActivityLogsTableStore(
    (state: IActivityLogsTableStore) => state.pinnedColumns,
  );
  const setPinnedColumns = useActivityLogsTableStore(
    (state: IActivityLogsTableStore) => state.setPinnedColumns,
  );
  const hiddenColumns = useActivityLogsTableStore(
    (state: IActivityLogsTableStore) => state.hiddenColumns,
  );
  const setHiddenColumns = useActivityLogsTableStore(
    (state: IActivityLogsTableStore) => state.setHiddenColumns,
  );

  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  const handleConfirmReset = () => {
    resetView();
    setIsResetModalOpen(false);
  };

  const onPageIndexChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => {
    setFilters({ ...filters, page: page + 1 });
  };

  const onPageSizeChange = (event: {
    target: {
      value: string;
    };
  }) => {
    setFilters({ ...filters, limit: toNumber(event.target.value), page: 1 });
  };

  const paginationModel = useMemo(
    () => ({
      page: filters.page - 1,
      pageSize: filters.limit || 100,
    }),
    [filters.page, filters.limit],
  );

  const sortModel = useMemo(
    () =>
      filters.sort_by
        ? [
            {
              field: filters.sort_by,
              sort: (filters.sort_order ?? null) as GridSortDirection,
            },
          ]
        : [],
    [filters.sort_by, filters.sort_order],
  );

  const handleChangeOrder = (
    field: ActivityLogsColumnNames,
    sort: GridSortDirection,
  ) => {
    setFilters({
      ...filters,
      sort_by: field,
      sort_order: (sort ?? 'desc') as SORT_ORDER,
      page: 1,
    });
  };

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    if (model.pageSize !== filters.limit) {
      onPageSizeChange({
        target: { value: String(model.pageSize) },
      });
    } else if (model.page !== filters.page - 1) {
      onPageIndexChange(null, model.page);
    }
  };

  const runtimeColumns = useMemo<GridColDef<IActivityLogsTableEntry>[]>(
    () => [
      {
        field: 'title',
        headerName: t('activityLog.table.header.title'),
        flex: 1.2,
        minWidth: 160,
        sortable: false,

        renderCell: params => (
          <Box
            sx={{
              position: 'relative',
              pl: 2,
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: 4,
                top: '15%',
                width: 4,
                height: '70%',
                borderRadius: 999,
                bgcolor: params.row.editable
                  ? theme.palette.primary.main
                  : theme.palette.secondary.main,
              }}
            />
            <TruncatedTextWithConditionalTooltip
              text={params.row.title || ''}
              variant='body2'
              width='100%'
            />
          </Box>
        ),
      },
      {
        field: 'timestamp',
        headerName: t('activityLog.table.header.timestamp'),
        flex: 1,
        minWidth: 130,
        sortable: false,

        renderCell: params => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <TruncatedTextWithConditionalTooltip
              text={formatDateShort(params.row.timestamp)}
              variant='body2'
              width='100%'
            />
          </Box>
        ),
      },
      {
        field: 'recorded_minutes',
        headerName: t('activityLog.table.header.minutes'),
        width: 110,
        sortable: false,

        renderCell: params => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <TruncatedTextWithConditionalTooltip
              text={String(params.row.recorded_minutes || '0')}
              variant='body2'
              width='100%'
            />
          </Box>
        ),
      },
      {
        field: 'provider',
        headerName: t('activityLog.table.header.provider'),
        flex: 1.2,
        minWidth: 160,
        sortable: false,

        renderCell: params => (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <TruncatedTextWithConditionalTooltip
              text={params.row.provider_name || ''}
              variant='body2'
              width='100%'
            />
            {density !== 'dense' && (
              <TruncatedTextWithConditionalTooltip
                text={params.row.provider_practice_name || ''}
                variant='caption'
                color='textSecondary'
                width='100%'
              />
            )}
          </Box>
        ),
      },
      {
        field: 'created_at',
        headerName: t('activityLog.table.header.createdAt'),
        width: 140,
        sortable: false,
        renderCell: params => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
            }}
          >
            {formatDateShort(params.row.created_at)}
          </Box>
        ),
      },
      {
        field: 'related_entity',
        headerName: t('activityLog.table.header.relatedEntity'),
        flex: 1.2,
        minWidth: 160,
        sortable: false,

        renderCell: params => (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <TruncatedTextWithConditionalTooltip
              text={params.row.entity_names || ''}
              variant='body2'
              width='100%'
            />
            {density !== 'dense' && (
              <TruncatedTextWithConditionalTooltip
                text={params.row.entity_types || ''}
                variant='caption'
                color='textSecondary'
                width='100%'
              />
            )}
          </Box>
        ),
      },
      {
        field: 'action',
        headerName: '',
        width: 140,
        sortable: false,

        align: 'right',
        renderCell: params => (
          <ActivityLogsRowActions row={params.row} disabled={disabled} />
        ),
      },
      {
        field: GRID_DETAIL_PANEL_TOGGLE_FIELD,
        width: 50,
        sortable: false,

        resizable: false,
      },
    ],
    [disabled, density],
  );

  const columns = buildGridColumns<
    IActivityLogsTableEntry,
    PersistedActivityLogsColumn
  >(runtimeColumns, persistedColumns);

  const toPersistedColumn = (
    col: GridColDef<IActivityLogsTableEntry>,
  ): PersistedActivityLogsColumn => ({
    field: col.field,
    headerName: col.headerName,
    width: col.width,
    minWidth: col.minWidth,
    flex: col.flex,
    sortable: col.sortable,
    filterable: col.filterable,
    disableColumnMenu: col.disableColumnMenu,
    align: col.align,
    headerAlign: col.headerAlign,
  });

  const handleColumnOrderChange = (_params: GridColumnOrderChangeParams) => {
    const next = getReorderedColumns(apiRef, columns);
    setPersistedColumns(next.map(toPersistedColumn));
  };

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    const next = columns.map(col =>
      col.field === params.colDef.field
        ? { ...col, width: params.width, flex: undefined }
        : col,
    );
    setPersistedColumns(next.map(toPersistedColumn));
  };

  const handlePinnedColumnsChange = (model: GridPinnedColumnFields) => {
    setPinnedColumns({
      left: (model.left ?? []).slice(0, MAX_PINNED_COLUMNS),
      right: (model.right ?? []).slice(0, MAX_PINNED_COLUMNS),
    });
  };

  const columnVisibilityModel: Record<string, boolean> = Object.fromEntries(
    columns.map(col => [col.field, !hiddenColumns.includes(col.field)]),
  );

  const handleColumnVisibilityModelChange = (
    model: Record<string, boolean>,
  ) => {
    const allFields = columns
      .map(col => col.field)
      .filter(field => field !== GRID_DETAIL_PANEL_TOGGLE_FIELD);
    const nextHidden = allFields.filter(field => model[field] === false);
    const visibleCount = allFields.length - nextHidden.length;
    if (visibleCount < MIN_VISIBLE_COLUMNS) return;
    setHiddenColumns(nextHidden);
  };

  const MIN_VISIBLE_COLUMNS = 4;
  const visibleColumnsCount = Object.entries(columnVisibilityModel).filter(
    ([field, visible]) => visible && field !== GRID_DETAIL_PANEL_TOGGLE_FIELD,
  ).length;
  const canHideColumn = visibleColumnsCount > MIN_VISIBLE_COLUMNS;
  const columnsWithHideable = columns.map(col => ({
    ...col,
    hideable: canHideColumn,
  }));

  const getDetailPanelContent = ({
    row,
  }: GridRowParams<IActivityLogsTableEntry>) => (
    <Box sx={{ bgcolor: theme.palette.grey[100], p: 2 }}>
      <Typography
        variant='body1'
        sx={{ whiteSpace: 'pre-line', wordBreak: 'break-all' }}
      >
        {row.description || t('activityLog.table.noDescription')}
      </Typography>
    </Box>
  );

  return {
    apiRef,
    density,
    setDensity,
    columns: columnsWithHideable,
    paginationModel,
    handlePaginationModelChange,
    sortModel,
    handleChangeOrder,
    getDetailPanelContent,
    isResetModalOpen,
    setIsResetModalOpen,
    handleConfirmReset,
    pinnedColumns,
    handlePinnedColumnsChange,
    columnVisibilityModel,
    handleColumnVisibilityModelChange,
    handleColumnOrderChange,
    handleColumnWidthChange,
  };
};
